// Slice B2.4-C of docs/planning/activity_dock_workflow_facilitation.md:
// React subscription to the `ACTIVITY_DOCK_PEEK_EVENT` channel B2.4-B
// emits, with in-session aggregation via the pure helpers in
// `src/lib/activityDock/peekStats.ts`.
//
// Usage discipline:
//
//   - Mount once per surface that wants to observe live peek activity.
//     The dev panel today is the only consumer; if a second one is
//     ever added it gets its own independent counters (the event
//     fan-out is window-level so both subscribers see the same fires).
//   - `clear()` resets the session log to empty — useful when the
//     reviewer wants a fresh window after changing the assist level
//     or impersonating a different role to retest.
//   - Hook is SSR-safe: when `window` is undefined the listener
//     install no-ops and the returned `summary` is the empty summary.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ACTIVITY_DOCK_PEEK_EVENT,
  type ActivityDockPeekEventDetail,
} from "./activityDockPeekEvent";
import {
  appendPeek,
  emptyPeekStatsState,
  summarizePeekStats,
  type PeekStatsState,
  type PeekStatsSummary,
} from "@/lib/activityDock/peekStats";

export type UseActivityDockPeekStatsResult = {
  state: PeekStatsState;
  summary: PeekStatsSummary;
  clear: () => void;
};

export function useActivityDockPeekStats(): UseActivityDockPeekStatsResult {
  const [state, setState] = useState<PeekStatsState>(emptyPeekStatsState);

  useEffect(() => {
    if (typeof window === "undefined") return;
    function handler(event: Event): void {
      // Defensive guard: another module on the same channel name could
      // dispatch a non-CustomEvent. Drop anything without a detail of
      // the expected shape rather than poisoning the log.
      const detail = (event as CustomEvent<ActivityDockPeekEventDetail>).detail;
      if (!detail || typeof detail !== "object") return;
      if (typeof detail.reasonSectionId !== "string") return;
      if (typeof detail.mode !== "string") return;
      setState((current) => appendPeek(current, detail));
    }
    window.addEventListener(ACTIVITY_DOCK_PEEK_EVENT, handler);
    return () => window.removeEventListener(ACTIVITY_DOCK_PEEK_EVENT, handler);
  }, []);

  const summary = useMemo(() => summarizePeekStats(state), [state]);
  const clear = useCallback(() => setState(emptyPeekStatsState()), []);

  return { state, summary, clear };
}
